/**
 * Cookie consent storage (.ru segment, ФЗ № 152-ФЗ + ФЗ № 156-ФЗ).
 *
 * Three categories:
 * - `essential`: always true (auth, CSRF, balancer, consent flag itself) — no choice required.
 * - `functional`: language, theme, UI preferences. Defaults to false until user opts in.
 * - `analytics`: Amplitude (cross-border transfer to USA). Defaults to false; must be explicitly accepted.
 *
 * Choice is persisted in localStorage with a version. Bumping CONSENT_VERSION re-shows
 * the banner to all users so they can re-confirm against the new wording / scope.
 */

import { isRuRegion } from './region';

export const CONSENT_VERSION = '2026-05-07';
const STORAGE_KEY = 'cookie_consent_v1';

export interface CookieConsent {
  essential: true;
  functional: boolean;
  analytics: boolean;
  version: string;
  timestamp: string;
}

export const getCookieConsent = (): CookieConsent | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsent;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const setCookieConsent = (input: { functional: boolean; analytics: boolean }): CookieConsent => {
  const consent: CookieConsent = {
    essential: true,
    functional: input.functional,
    analytics: input.analytics,
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
  };
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
      window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: consent }));
    } catch {
      /* ignore quota / privacy mode errors */
    }
  }
  return consent;
};

export const acceptAllCookies = () => setCookieConsent({ functional: true, analytics: true });
export const acceptOnlyNecessary = () => setCookieConsent({ functional: false, analytics: false });

export const hasGivenCookieConsent = (): boolean => getCookieConsent() !== null;

export const hasAnalyticsConsent = (): boolean => {
  // .com segment never had a banner — analytics has been on by default historically.
  // We do not change behaviour there.
  if (!isRuRegion()) return true;
  const consent = getCookieConsent();
  return consent?.analytics === true;
};

export const shouldShowCookieBanner = (): boolean => {
  if (!isRuRegion()) return false;
  return !hasGivenCookieConsent();
};
